/**
 * Currency Selector Component
 * Allows users to switch between AUD, USD, and GBP with exchange rate info
 * Base currency is USD (matches pricing API source)
 */

import React, { useState, useRef, useEffect } from 'react'
import { useCurrency, Currency } from '../../contexts/CurrencyContext'
import styles from './CurrencySelector.module.css'

export interface CurrencyMeta {
  code: Currency
  name: string
  flag: string
  rate: string
}

export const CURRENCIES: CurrencyMeta[] = [
  { code: 'USD', name: 'US Dollar', flag: '🇺🇸', rate: '1.00' },
  { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺', rate: '1.54' },
  { code: 'GBP', name: 'British Pound', flag: '🇬🇧', rate: '0.80' },
  { code: 'EUR', name: 'Euro', flag: '🇪🇺', rate: '0.95' },
  { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵', rate: '149.50' },
  { code: 'CNY', name: 'Chinese Yuan', flag: '🇨🇳', rate: '7.24' }
]

interface CurrencySelectorProps {
  compact?: boolean
}

export const COMPACT_CURRENCY_LABELS: Record<Currency, string> = {
  USD: 'US$',
  AUD: 'A$',
  GBP: 'GBP',
  EUR: 'EUR',
  JPY: 'JPY',
  CNY: 'CNY'
}

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ compact = false }) => {
  const { currency, setCurrency, getCurrencySymbol } = useCurrency()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentCurrency = CURRENCIES.find(c => c.code === currency)
  const compactCurrencyLabel = COMPACT_CURRENCY_LABELS[currency] || `${getCurrencySymbol()}${currency}`

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (curr: Currency) => {
    setCurrency(curr)
    setIsOpen(false)
  }

  return (
    <div className={styles.currencySelector} ref={dropdownRef}>
      <button
        className={`${styles.currencySelectorButton} ${compact ? styles.compactButton : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Change Currency"
      >
        {compact ? (
          <span className={styles.currencyCompactLabel}>{compactCurrencyLabel}</span>
        ) : (
          <>
            <span className={styles.currencyFlag}>{currentCurrency?.flag}</span>
            <span className={styles.currencyCode}>{currency}</span>
            <span className={styles.currencySymbol}>{getCurrencySymbol()}</span>
          </>
        )}
        <svg
          className={`${styles.currencyDropdownIcon} ${isOpen ? styles.open : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.currencyDropdown}>
          <div className={styles.currencyDropdownHeader}>
            <span className={styles.dropdownTitle}>Select Currency</span>
            <span className={styles.dropdownSubtitle}>Prices convert from USD</span>
          </div>
          {CURRENCIES.map((curr) => (
            <button
              key={curr.code}
              className={`${styles.currencyOption} ${curr.code === currency ? styles.active : ''}`}
              onClick={() => handleSelect(curr.code)}
            >
              <span className={styles.currencyFlag}>{curr.flag}</span>
              <div className={styles.currencyInfo}>
                <span className={styles.currencyName}>{curr.name}</span>
                <span className={styles.currencyCodeSmall}>{curr.code} (1 USD = {curr.rate} {curr.code})</span>
              </div>
              {curr.code === currency && (
                <svg
                  className={styles.currencyCheck}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default CurrencySelector
